import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus,
  MagnifyingGlass as Search,
  FunnelSimple as Filter,
  DownloadSimple as Download,
  Users,
  Buildings as Building2,
  ShieldCheck,
  Envelope as Mail,
  Phone,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StatCard } from '@/components/shared/StatCard';
import { RowActions } from '@/components/shared/RowActions';
import { useContactsStore, type Contact } from '@/lib/contactsStore';
import { searchContacts } from '@/lib/contactsApi';
import { useDebounce } from '@/lib/useDebounce';
import { listConflictReviews, type ConflictReview } from '@/lib/conflictReviewsApi';
import { useAuthStore } from '@/features/auth/store';
import { EditContactModal } from '@/components/shared/EditContactModal';

type Tab = 'all' | 'lay' | 'solicitors' | 'experts' | 'witnesses' | 'companies' | 'conflicts';

const TYPE_LABEL: Record<string, string> = {
  lay: 'Lay client', solicitor: 'Instructing solicitor', expert: 'Expert',
  witness: 'Witness', company: 'Company',
};

const PAGE_SIZE = 5;

function exportContactsCsv(contacts: ReturnType<typeof useContactsStore.getState>['contacts']) {
  const header = ['Name', 'Type', 'Organisation', 'Email', 'Phone', 'Matters', 'KYC'];
  const rows = contacts.map((c) => [
    c.name, TYPE_LABEL[c.type] ?? c.type, c.org, c.email, c.phone, String(c.cases), c.kyc,
  ]);
  const csv = [header, ...rows].map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = `contacts-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function ContactsPage() {
  const storeContacts = useContactsStore((s) => s.contacts);
  const fetchContacts = useContactsStore((s) => s.fetchContacts);
  const [tab, setTab] = useState<Tab>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [kycFilter, setKycFilter] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [editContact, setEditContact] = useState<Contact | null>(null);
  const updateContact = useContactsStore((s) => s.updateContact);
  const removeContact = useContactsStore((s) => s.deleteContact);
  const permissions = useAuthStore((s) => s.permissions);
  const canDelete = permissions.includes('contact.delete');
  const navigate = useNavigate();
  // Rendered for admin AND clerk — navigate within the current role's tree, not a hardcoded /admin.
  const memberType = useAuthStore((s) => s.user?.memberType);
  const basePath = memberType === 'senior_clerk' || memberType === 'junior_clerk' ? '/clerk' : '/admin';
  // P0-2: a conflict is a regulated matter. This tab reads the REAL server conflict-review
  // queue (never a browser-local store), and decisions are recorded on the canonical
  // Conflict Reviews screen — never here, so there is one conflict-decision surface, not two.
  const canSeeConflicts = memberType === 'admin' || memberType === 'senior_clerk';
  const conflictReviewsQ = useQuery({
    queryKey: ['conflict-reviews', 'pending'],
    queryFn: () => listConflictReviews('pending'),
    enabled: canSeeConflicts,
  });
  const pendingReviews: ConflictReview[] = conflictReviewsQ.data ?? [];

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  // When the user types, hit the server search so EVERY contact is found — not
  // just the ~100 loaded into the store — and so phone/firm match too. Below 2
  // chars we stay on the in-memory store for an instant list.
  const debouncedSearch = useDebounce(search.trim(), 300);
  const searching = debouncedSearch.length >= 2;
  const searchQ = useQuery({
    queryKey: ['contacts-search', debouncedSearch],
    queryFn: () => searchContacts(debouncedSearch, undefined, 50),
    enabled: searching,
    staleTime: 30_000,
  });

  if (searchQ.isError) {
    return <QueryErrorState what="contacts" />;
  }

  const filtered = useMemo(() => {
    const source = searching ? (searchQ.data ?? []) : storeContacts;
    let data = tab === 'all' || tab === 'conflicts'
      ? source
      : source.filter((c) =>
          tab === 'lay' ? c.type === 'lay' :
          tab === 'solicitors' ? c.type === 'solicitor' :
          tab === 'experts' ? c.type === 'expert' :
          tab === 'witnesses' ? c.type === 'witness' :
          tab === 'companies' ? c.type === 'company' : true,
        );

    // Text matching is done by the server when searching; only filter client-side
    // for a 1-char query against the already-loaded store.
    if (!searching && search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.org.toLowerCase().includes(q) ||
          c.email.toLowerCase().includes(q),
      );
    }

    if (kycFilter) {
      data = data.filter((c) => c.kyc === kycFilter);
    }

    return data;
  }, [tab, search, searching, searchQ.data, kycFilter, storeContacts]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="Clients & Contacts"
        subtitle="Lay clients, instructing solicitors, experts, witnesses and companies"
        actions={
          <>
            <Button variant="outline" size="md" leftIcon={<Plus className="h-4 w-4" />} onClick={() => navigate(`${basePath}/contacts/new`)}>New company</Button>
            <Button variant="primary" size="md" leftIcon={<Plus className="h-4 w-4" />} onClick={() => navigate(`${basePath}/contacts/new`)}>New person</Button>
          </>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard icon={<Users className="h-4 w-4" />}        label="Total contacts"      value={String(storeContacts.length)} />
        <StatCard icon={<Building2 className="h-4 w-4" />}    label="Instructing firms"   value={String(new Set(storeContacts.filter((c) => c.type === 'solicitor').map((c) => c.org)).size)} hint="unique orgs" />
        <StatCard icon={<ShieldCheck className="h-4 w-4" />}  label="KYC valid"           value={String(storeContacts.filter((c) => c.kyc === 'Valid').length)} hint={storeContacts.length ? `${Math.round((storeContacts.filter((c) => c.kyc === 'Valid').length / storeContacts.length) * 100)}%` : '0%'} />
        <StatCard icon={<ShieldCheck className="h-4 w-4" />}  label="KYC expiring (30d)"  value={String(storeContacts.filter((c) => c.kyc === 'Expiring').length)} />
      </div>

      {/* Tabs */}
      <div className="no-scrollbar -mb-px mb-4 flex items-center gap-1 overflow-x-auto overflow-y-hidden border-b border-border">
        {([
          { key: 'all',          label: 'All',                    count: storeContacts.length },
          { key: 'lay',          label: 'Lay clients',            count: storeContacts.filter((c) => c.type === 'lay').length },
          { key: 'solicitors',   label: 'Instructing solicitors', count: storeContacts.filter((c) => c.type === 'solicitor').length },
          { key: 'experts',      label: 'Experts',                count: storeContacts.filter((c) => c.type === 'expert').length },
          { key: 'witnesses',    label: 'Witnesses',              count: storeContacts.filter((c) => c.type === 'witness').length },
          { key: 'companies',    label: 'Companies',              count: storeContacts.filter((c) => c.type === 'company').length },
          { key: 'conflicts',    label: 'Conflict checks',        count: pendingReviews.length },
        ] as { key: Tab; label: string; count: number }[]).map((t) => (
          <button
            key={t.key}
            onClick={() => { setTab(t.key); setPage(1); }}
            className={`shrink-0 border-b-2 px-4 py-2.5 text-sm font-medium transition ${
              tab === t.key
                ? 'border-brand-600 text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
            <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] tabular text-muted-foreground">
              {t.count}
            </span>
          </button>
        ))}
      </div>

      {tab !== 'conflicts' ? (
        <>
          <Card className="mb-4 p-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex flex-1 items-center gap-2 rounded-md border border-border bg-card px-3">
                <Search className="h-4 w-4 text-muted-foreground" />
                <input
                  type="search"
                  placeholder="Search by name, organisation, email or phone…"
                  className="h-9 flex-1 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                />
                {searching && searchQ.isFetching && (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
                )}
              </div>
              <Button
                variant={filtersOpen || kycFilter ? 'primary' : 'outline'}
                size="md"
                leftIcon={<Filter className="h-4 w-4" />}
                onClick={() => setFiltersOpen((o) => !o)}
              >
                Filters{kycFilter ? ' · 1' : ''}
              </Button>
              <Button variant="outline" size="md" leftIcon={<Download className="h-4 w-4" />} onClick={() => exportContactsCsv(storeContacts)}>Export CSV</Button>
            </div>
            {filtersOpen && (
              <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-border pt-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-muted-foreground">KYC status</span>
                  <div className="flex flex-wrap items-center gap-1">
                    {(['All', 'Valid', 'Pending', 'In progress', 'Expiring', 'Rejected'] as const).map((v) => (
                      <button
                        key={v}
                        onClick={() => { setKycFilter(v === 'All' ? null : v); setPage(1); }}
                        className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                          (v === 'All' && kycFilter === null) || kycFilter === v
                            ? 'bg-brand-600 text-white'
                            : 'bg-muted text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
                {kycFilter && (
                  <button
                    onClick={() => { setKycFilter(null); setPage(1); }}
                    className="text-xs text-muted-foreground underline hover:text-foreground"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </Card>

          <Card>
            <div className="overflow-x-auto">
              {/* A table cannot reflow: on a phone its right-hand columns ran off the screen with no way to reach them. */}
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="w-10 whitespace-nowrap">Actions</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Organisation</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Matters</th>
                    <th>KYC</th>
                    <th className="w-10 whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.filter((c) => !c.id.startsWith('temp-')).map((c) => (
                    <tr key={c.id} className="cursor-pointer" onClick={() => navigate(`${basePath}/contacts/${c.id}`)}>
                      <td onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" className="h-4 w-4 rounded border-border text-brand-600 dark:text-brand-400 focus:ring-brand-100" />
                      </td>
                      <td className="font-medium text-foreground">
                        <div className="flex items-center gap-2.5">
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-50 text-[10px] font-semibold text-brand-700 dark:text-brand-300">
                            {c.name.split(' ').map((p) => p[0]).join('').slice(0, 2)}
                          </div>
                          {c.name}
                        </div>
                      </td>
                      <td><Badge>{TYPE_LABEL[c.type]}</Badge></td>
                      <td className="text-muted-foreground">{c.org}</td>
                      <td className="text-muted-foreground">
                        <Mail className="mr-1 inline h-3 w-3" />
                        {c.email}
                      </td>
                      <td className="text-xs text-muted-foreground">
                        <Phone className="mr-1 inline h-3 w-3" />
                        {c.phone}
                      </td>
                      <td className="text-xs text-muted-foreground">{c.cases}</td>
                      <td>
                        <Badge variant={c.kyc === 'Valid' ? 'success' : c.kyc === 'Expiring' ? 'warning' : 'default'}>
                          {c.kyc}
                        </Badge>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-0.5">
                          <button
                            type="button"
                            title="Email"
                            aria-label="Email"
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                            onClick={() => { window.location.href = `mailto:${c.email}`; }}
                          >
                            <Mail className="h-4 w-4" />
                          </button>
                          <RowActions
                            onView={() => navigate(`${basePath}/contacts/${c.id}`)}
                            onEdit={() => setEditContact(c)}
                            onDelete={canDelete ? () => {
                              removeContact(c.id);
                              toast.success(`Contact "${c.name}" deleted`);
                            } : undefined}
                            deleteConfirm={{
                              title: 'Delete contact',
                              message: `Delete contact "${c.name}"? This cannot be undone.`,
                              confirmLabel: 'Delete',
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                  {paged.length === 0 && (
                    <tr>
                      <td colSpan={9} className="py-12 text-center text-sm text-muted-foreground">
                        No contacts match your search.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between border-t border-border px-4 py-3 text-xs text-muted-foreground">
              <div>
                Showing <span className="text-foreground">{paged.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)}</span> of <span className="text-foreground">{filtered.length}</span> contacts
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="xs" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1}>Previous</Button>
                <span className="px-2 text-xs text-foreground">{safePage} / {totalPages}</span>
                <Button variant="outline" size="xs" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}>Next</Button>
              </div>
            </div>
          </Card>
        </>
      ) : (
        <div className="space-y-4">
          {!canSeeConflicts ? (
            <Card className="px-6 py-10 text-center">
              <ShieldCheck className="mx-auto h-10 w-10 text-muted-foreground" />
              <div className="mt-3 text-sm font-semibold text-foreground">Conflict reviews are managed by administrators and senior clerks</div>
              <div className="mt-1 text-xs text-muted-foreground">You do not have access to the conflict-review queue.</div>
            </Card>
          ) : conflictReviewsQ.isError ? (
            <QueryErrorState what="conflict reviews" onRetry={() => conflictReviewsQ.refetch()} />
          ) : (
            <Card>
              <div className="flex items-center justify-between border-b border-border px-6 py-3">
                <div className="flex items-center gap-2 text-sm">
                  <ShieldCheck className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  <span className="font-medium text-foreground">
                    {conflictReviewsQ.isLoading
                      ? 'Loading conflict reviews…'
                      : `${pendingReviews.length} conflict${pendingReviews.length !== 1 ? 's' : ''} awaiting a decision`}
                  </span>
                </div>
                <Button variant="primary" size="sm" onClick={() => navigate(`${basePath}/conflict-reviews`)}>
                  Review &amp; decide
                </Button>
              </div>
              <div className="divide-y divide-border">
                {!conflictReviewsQ.isLoading && pendingReviews.length === 0 && (
                  <div className="px-6 py-10 text-center">
                    <ShieldCheck className="mx-auto h-10 w-10 text-emerald-500" />
                    <div className="mt-3 text-sm font-semibold text-foreground">No conflicts awaiting a decision</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      Conflicts are raised automatically at intake and on re-check. Decisions are recorded — with the regulatory basis and a written reason — on the Conflict Reviews screen.
                    </div>
                  </div>
                )}
                {pendingReviews.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => navigate(`${basePath}/conflict-reviews`)}
                    className="flex w-full items-start gap-4 px-6 py-4 text-left hover:bg-muted/50"
                  >
                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400">
                      <ShieldCheck className="h-4 w-4" weight="fill" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <Badge variant={r.severity === 'critical' ? 'danger' : r.severity === 'warning' ? 'warning' : 'info'}>
                          {r.severity}
                        </Badge>
                        <span className="truncate text-sm font-medium text-foreground">{r.case.caseName}</span>
                        <span className="text-xs text-muted-foreground">· {r.case.caseNumber}</span>
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {r.conflicts.length} conflict{r.conflicts.length !== 1 ? 's' : ''} detected — open the review to record a decision.
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      <EditContactModal
        open={!!editContact}
        contact={editContact}
        onClose={() => setEditContact(null)}
        onSave={(id, patch) => { updateContact(id, patch); setEditContact(null); }}
      />
    </div>
  );
}
